<!--
  FixedPriceMarginWarning.vue (Bug Intake #524, Jake 2026-10-23).

  Review & Launch notice: fixed-price sizes whose ESTIMATED margin is below the
  floor or whose price is below the ESTIMATED cost. It exists because PR 4738
  gave embroidery a size-based decoration cost estimate, which quietly cut the
  margin of fixed prices set before it. Source of truth is the server
  (GET /api/stores/<id>/fixed_price_margin/, built on price_breaks costs); the
  Pricing tab's live "Below cost" tag is the per-size view of the same numbers.

  WARN ONLY: it never disables Launch. "Got it" stores the server's signature of
  the flagged set (fixedPriceMarginAck.js), so it does not re-nag until the
  flagged set changes.

  Renders nothing while loading, on a 403/404 (a caller without product.edit
  must not see cost-derived margins) or when nothing is flagged.
-->
<template>
  <section
    v-if="visible"
    class="fpm"
    data-testid="fixed-price-margin-warning"
    role="status"
  >
    <div class="fpm__head">
      <span class="tf-icon fpm__icon" aria-hidden="true">warning_amber</span>
      <div class="fpm__copy">
        <h3 class="fpm__title" data-testid="fixed-price-margin-title">
          {{ title }}
        </h3>
        <p class="fpm__sub" data-testid="fixed-price-margin-sub">
          Margins here are estimates: embroidery cost is estimated from the
          design size, and the estimate is likely high. Check the Pricing tab
          before you launch. This does not block launching.
        </p>
      </div>
    </div>

    <ul class="fpm__list">
      <li
        v-for="p in products"
        :key="p.campaign_product_id"
        class="fpm__item"
        :data-testid="`fixed-price-margin-product-${p.campaign_product_id}`"
      >
        <a
          class="fpm__link"
          href="#"
          :data-testid="`fixed-price-margin-link-${p.campaign_product_id}`"
          @click.prevent="openPricing(p)"
        >{{ p.product_name || "Untitled product" }}</a>
        <span class="fpm__sizes">
          <span
            v-for="s in p.sizes"
            :key="s.size"
            class="fpm__size"
            :class="{ 'fpm__size--below': s.below_cost }"
            :data-testid="`fixed-price-margin-size-${p.campaign_product_id}-${s.size}`"
          >{{ sizeLabel(s) }}</span>
        </span>
      </li>
    </ul>

    <div class="fpm__actions">
      <button
        type="button"
        class="fpm__ack"
        data-testid="fixed-price-margin-dismiss"
        @click="acknowledge"
      >
        Got it
      </button>
    </div>
  </section>
</template>

<script>
import api from "@/services/api";
import { formatBasisPoints } from "../StoreProducts/priceBreakdown";
import {
  acknowledgeMarginWarning,
  isMarginWarningAcknowledged,
} from "./fixedPriceMarginAck";

export default {
  name: "FixedPriceMarginWarning",
  props: {
    storeId: { type: [Number, String], required: true },
  },
  data() {
    return { products: [], signature: "", floorBp: null, acknowledged: false };
  },
  computed: {
    visible() {
      return this.products.length > 0 && !this.acknowledged;
    },
    sizeCount() {
      return this.products.reduce((n, p) => n + p.sizes.length, 0);
    },
    title() {
      const n = this.products.length;
      const floor = formatBasisPoints(this.floorBp);
      const what = n === 1 ? "1 fixed-price product has" : `${n} fixed-price products have`;
      return `${what} sizes below cost or under a ${floor} margin (estimated)`;
    },
  },
  watch: {
    storeId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      const requested = this.storeId;
      try {
        const { data } = await api.get(`/stores/${requested}/fixed_price_margin/`);
        if (requested !== this.storeId) return;
        this.products = Array.isArray(data?.products) ? data.products : [];
        this.signature = typeof data?.signature === "string" ? data.signature : "";
        this.floorBp = Number.isFinite(data?.margin_floor_bp) ? data.margin_floor_bp : null;
        this.acknowledged = isMarginWarningAcknowledged(requested, this.signature);
      } catch {
        // No access (403/404) or a failed read: this notice is advisory, so
        // it stays quiet rather than blocking or alarming the page.
        this.products = [];
      }
    },
    sizeLabel(s) {
      const money = `$${(s.price_cents / 100).toFixed(2)}`;
      if (s.below_cost) return `${s.size} ${money}: below cost (est.)`;
      return `${s.size} ${money}: ${formatBasisPoints(s.margin_bp)} margin (est.)`;
    },
    openPricing(p) {
      this.$router.push(`/distributor/product-editor/${p.campaign_product_id}/pricing`);
    },
    acknowledge() {
      acknowledgeMarginWarning(this.storeId, this.signature);
      this.acknowledged = true;
    },
  },
};
</script>

<style scoped>
.fpm {
  border: 1px solid rgba(255, 168, 0, 0.5);
  background: linear-gradient(0deg, rgba(255, 168, 0, 0.05), rgba(255, 168, 0, 0.05)), #fff;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fpm__head { display: flex; gap: 12px; align-items: flex-start; }
.fpm__icon { color: #b45f00; }
.fpm__title { margin: 0 0 4px 0; font-size: 16px; font-weight: 600; }
.fpm__sub { margin: 0; color: #4b5563; font-size: 14px; }
.fpm__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.fpm__item { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: baseline; font-size: 14px; }
.fpm__link { font-weight: 600; cursor: pointer; }
.fpm__sizes { display: inline-flex; flex-wrap: wrap; gap: 6px; }
.fpm__size {
  padding: 1px 8px; border-radius: 999px; font-size: 12px;
  background: #fff4e0; border: 1px solid #f0c27a; color: #8a4b00;
}
.fpm__size--below { background: #fdecec; border-color: #e9a1a1; color: #9b1c1c; }
.fpm__actions { display: flex; justify-content: flex-end; }
.fpm__ack {
  border: 1px solid #d1d5db; background: #fff; border-radius: 8px;
  padding: 6px 14px; font-size: 14px; cursor: pointer;
}
</style>
